import { useState } from 'react';
import { X, Shuffle } from 'lucide-react';
import { getTeam } from '@/lib/teams';
import { useRoutingPolicy, type RoutingPolicy, type RoutingStrategy } from '@/lib/routing';
import { cn } from '@/lib/utils';

const STRATEGIES: { id: RoutingStrategy; label: string; desc: string }[] = [
  { id: 'manual', label: 'Manual', desc: 'No auto-assignment' },
  { id: 'round-robin', label: 'Round-robin', desc: 'Cycle through members evenly' },
  { id: 'load-based', label: 'Load-based', desc: 'Assign to the least-loaded member' },
  { id: 'skills', label: 'Skills-based', desc: 'Match category → member, then load' },
];

export function RoutingEditor({
  open,
  teamId,
  teamName,
  onClose,
}: {
  open: boolean;
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const { policy, save } = useRoutingPolicy(teamId);
  const [draft, setDraft] = useState<RoutingPolicy>(policy);
  const members = getTeam(teamId)?.members ?? [];

  if (!open) return null;

  const setSkill = (member: string, csv: string) =>
    setDraft((d) => ({
      ...d,
      skills: { ...(d.skills ?? {}), [member]: csv.split(',').map((s) => s.trim()).filter(Boolean) },
    }));

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-6 pt-[10vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold"><Shuffle className="h-4 w-4 text-primary" /> Auto-routing</div>
            <div className="text-xs text-muted-foreground">How new {teamName} tickets get assigned</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3 p-5">
          {STRATEGIES.map((s) => (
            <button
              key={s.id}
              onClick={() => setDraft((d) => ({ ...d, strategy: s.id }))}
              className={cn(
                'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition',
                draft.strategy === s.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/40',
              )}
            >
              <span className={cn('mt-1 h-3 w-3 flex-shrink-0 rounded-full border', draft.strategy === s.id ? 'border-primary bg-primary' : 'border-input')} />
              <div>
                <div className="text-sm font-medium">{s.label}</div>
                <div className="text-xs text-muted-foreground">{s.desc}</div>
              </div>
            </button>
          ))}

          {draft.strategy === 'skills' && (
            <div className="space-y-2 rounded-xl border border-border p-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Member skills (categories)</div>
              {members.map((m) => (
                <div key={m} className="flex items-center gap-2">
                  <span className="w-20 flex-shrink-0 text-sm">{m}</span>
                  <input
                    value={(draft.skills?.[m] ?? []).join(', ')}
                    onChange={(e) => setSkill(m, e.target.value)}
                    placeholder="e.g. Network, Access"
                    className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted">Cancel</button>
          <button onClick={() => { save(draft); onClose(); }} className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">Save</button>
        </div>
      </div>
    </div>
  );
}
